import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'
import type { PageResponse } from '@/shared/api/types'

/**
 * The War Room — `/api/v1/warroom`.
 *
 * ## Two shapes here encode rules the UI must not soften
 *
 * **Engagement metrics are nullable.** `likes: null` means the platform published no number,
 * not that nobody liked it. A client that coalesced it to 0 would render "we do not know" as
 * "nobody cared" — and only one of those is a reason to stand down.
 *
 * **Every deployment carries `approvedBy`, and it is never null.** There is no shape in this
 * file for a post nobody approved, because there is no such row in the database: the column is
 * NOT NULL. That is the Centaur model, and the UI's job is to show whose name is on it.
 */

const base = '/warroom'

export interface Persona {
  id: string
  code: string
  name: string
  /** What this voice is FOR. Shown beside every draft, since approval happens at speed. */
  functionNote: string
  guardrails: string | null
  active: boolean
  sortOrder: number
}

export interface Platform {
  code: string
  label: string
  /** False for a platform we can watch but not reply on. Not a fault — say so, don't hide it. */
  canPost: boolean
  accountConfigKeys: string[]
}

export interface Account {
  id: string
  providerCode: string
  /** Credentials live in `integration`; this is only the pointer. */
  integrationId: string | null
  /**
   * Non-secret per-account platform config — which Page, which IG user, which channel.
   * Keys are exactly what the platform declares in `accountConfigKeys`; the server rejects
   * anything else. Never a token: this is returned to every holder of `warroom:read`.
   */
  config: Record<string, string>
  handle: string
  displayName: string | null
  personaId: string | null
  personaName: string | null
  subjectId: string | null
  dailyCap: number
  postedToday: number
  countedOn: string | null
  active: boolean
  lastError: string | null
}

export interface Watch {
  id: string
  subjectId: string
  subjectName: string | null
  label: string
  terms: string
  platforms: string
  velocityEngagements: number
  velocityMinutes: number
  pollSeconds: number
  active: boolean
  lastPolledAt: string | null
  /** OK or ERROR. A watch that errored is NOT a watch that found nothing. */
  lastStatus: string | null
  lastError: string | null
}

export interface Thread {
  id: string
  watchId: string | null
  subjectId: string
  subjectName: string | null
  platformCode: string
  externalId: string | null
  url: string | null
  authorHandle: string | null
  authorDisplay: string | null
  title: string | null
  engagements: number | null
  replies: number | null
  reposts: number | null
  likes: number | null
  /** Engagements per minute. The feed is ranked by this, not by arrival. */
  velocity: number | null
  statusCode: string
  firstSeenAt: string
  lastPolledAt: string | null
  editedSeen: boolean
  dismissedReason: string | null
  dismissedAt: string | null
  /** The latest reading. Null means nobody has looked yet — not that it is harmless. */
  threatScore: number | null
  sentimentCode: string | null
  draftCount: number
  deployedCount: number
}

export interface Post {
  id: string
  externalId: string | null
  authorHandle: string | null
  authorDisplay: string | null
  body: string
  postedAt: string | null
  capturedAt: string
  likes: number | null
  replies: number | null
  /** One of ours, deployed from here. */
  ours: boolean
  /** Engaged without commenting — PART IV.3. */
  lurker: boolean
}

export interface Assessment {
  id: string
  threatScore: number
  sentimentCode: string
  framingCode: string
  fallaciesJson: string
  rationale: string
  advisorRunId: string | null
  assessedBy: string | null
  assessedAt: string
}

export interface Draft {
  id: string
  threadId: string
  inReplyToPostId: string | null
  personaId: string | null
  personaName: string | null
  tacticCode: string
  body: string
  rationale: string
  citedItemIdsJson: string
  assetId: string | null
  advisorRunId: string | null
  statusCode: string
  /** What the model wrote, when a human changed it. */
  editedFrom: string | null
  reviewedBy: string | null
  reviewedAt: string | null
  reviewNote: string | null
  createdAt: string
}

export interface Deployment {
  /** Sends attempted. Terminal once it reaches the configured ceiling. */
  attempts: number
  /** When the dispatcher will try again, or null when it has stopped. */
  nextAttemptAt: string | null
  id: string
  draftId: string
  threadId: string
  accountId: string
  accountHandle: string | null
  personaId: string | null
  personaName: string | null
  /** NEVER null. The whole point of the ledger. */
  approvedBy: string
  approvedAt: string
  /** The exact bytes sent, frozen at approval — not a pointer at the mutable draft. */
  postedBody: string
  scheduledAt: string
  jitterSeconds: number | null
  dispatchedAt: string | null
  externalPostId: string | null
  externalUrl: string | null
  statusCode: string
  failureReason: string | null
  tacticCode: string | null
}

export interface Coordination {
  id: string
  subjectId: string
  subjectName: string | null
  /** The repeated string. This IS the evidence and it is quoted, never summarised. */
  phrase: string
  distinctAuthors: number
  threadCount: number
  windowMinutes: number
  /** 0..100, capped at 90. Never "confirmed": this counts strings, not intent. */
  confidence: number
  firstSeenAt: string
  lastSeenAt: string
  stoodDownBy: string | null
  stoodDownAt: string | null
  stoodDownReason: string | null
}

export interface Lurker {
  id: string
  handle: string
  signalCode: string
  postId: string | null
  outreachDraftId: string | null
  noticedAt: string
}

/**
 * What the council was asked, and what came of it.
 *
 * `status` separates the three outcomes that used to render identically as an unchanged
 * screen: PENDING (still working), EMPTY (answered, proposed nothing — including a deliberate
 * recommendation to leave the thread alone), FAILED (something went wrong, `note` says what).
 * MATERIALISED means the reading or the drafts below came from it.
 */
export interface CouncilRun {
  runId: string
  /** Null for a subject-wide question — exactly one of threadId/subjectId is set. */
  threadId: string | null
  subjectId: string | null
  /** The question, for a free-form subject run. Null for the two fixed-shape playbooks. */
  question: string | null
  playbookCode: string
  status: 'PENDING' | 'MATERIALISED' | 'EMPTY' | 'FAILED'
  requestedAt: string
  settledAt: string | null
  produced: number
  note: string | null
}

/**
 * An image or video the thread carried.
 *
 * `status` carries a distinction the UI must not flatten:
 * - `AVAILABLE` — we know where it is and have not spent the bandwidth. A button.
 * - `STORED`    — we hold a copy; `url` is a short-lived link to it.
 * - `SKIPPED`   — past the size ceiling. Nothing is wrong and no retry helps.
 * - `FAILED`    — the evidence is gone, usually an expired link on a deleted post.
 *
 * Rendering AVAILABLE and FAILED alike would let a room believe it still held something it
 * had already lost.
 */
export interface ThreadMedia {
  id: string
  postId: string | null
  kind: 'IMAGE' | 'VIDEO'
  status: 'AVAILABLE' | 'STORED' | 'SKIPPED' | 'FAILED'
  /** A presigned link, minted per response and short-lived. Null until something is stored. */
  url: string | null
  sourceUrl: string
  contentType: string | null
  sizeBytes: number | null
  failureReason: string | null
  capturedAt: string
  downloadedAt: string | null
}

export interface ThreadDetail {
  thread: Thread
  posts: Post[]
  assessments: Assessment[]
  drafts: Draft[]
  deployments: Deployment[]
  lurkers: Lurker[]
  coordination: Coordination[]
  councilRuns: CouncilRun[]
  media: ThreadMedia[]
}

export interface Dashboard {
  threadsLive: number
  /** Across the WHOLE feed, not across `topThreads`. */
  threadsUnscored: number
  threatsAboveThreshold: number
  draftsAwaiting: number
  deployedToday: number
  coordinationSignals: number
  deployThreshold: number
  topThreads: Thread[]
}

// ── the room ────────────────────────────────────────────────────────────────

export const getDashboard = () => api.get<Dashboard>(`${base}/dashboard`)

/**
 * The live feed, paged and searchable.
 *
 * These four lists are paged because they are the ones that grow without bound — a campaign
 * accumulates threads, drafts, deployments and coordination signals for its whole life. The
 * bounded lists (accounts, voices, watches, platforms) are deliberately NOT paged: a room has
 * a handful of each, and paging them would add a control that never has anything to do.
 *
 * Scope and search are both applied by the database. Filtering a page after it has been chosen
 * gives short pages and a total that counts rows the caller cannot see.
 */
export const listThreads = (p: ThreadQuery = {}) =>
  api.get<PageResponse<Thread>>(`${base}/threads${qs(p)}`)

export interface ThreadQuery {
  [key: string]: string | number | boolean | undefined
  /** Matches author handle, display name, the captured text and the URL. */
  q?: string
  status?: string
  platform?: string
  subjectId?: string
  /** true = has a recorded reading, false = nobody has scored it yet, omitted = both. */
  scored?: boolean
  /** A `ThreatBand.code` — matched against the thread's LATEST reading, never its history. */
  band?: string
  page?: number
  size?: number
}

export const getThread = (id: string) => api.get<ThreadDetail>(`${base}/threads/${id}`)

/** Pull a video the thread carried into our own storage. Images arrive already mirrored. */
export const downloadThreadMedia = (threadId: string, mediaId: string) =>
  api.post<ThreadMedia>(`${base}/threads/${threadId}/media/${mediaId}/download`)

export const captureThread = (body: unknown) => api.post<Thread>(`${base}/threads`, body)

export const addPost = (threadId: string, body: unknown) =>
  api.post<ThreadDetail>(`${base}/threads/${threadId}/posts`, body)

export const noteLurker = (threadId: string, body: unknown) =>
  api.post<ThreadDetail>(`${base}/threads/${threadId}/lurkers`, body)

export const refreshThread = (id: string) => api.post<Thread>(`${base}/threads/${id}/refresh`)

export const dismissThread = (id: string, reason: string) =>
  api.post<Thread>(`${base}/threads/${id}/dismiss`, { reason })

export const escalateThread = (id: string) => api.post<Thread>(`${base}/threads/${id}/escalate`)

// ── triage and drafting: reading and proposing. Neither posts anything. ──────

export const requestAssessment = (id: string, question?: string) =>
  api.post<{ advisorRunId: string }>(`${base}/threads/${id}/assess`, { question })

export const recordAssessment = (id: string, body: unknown) =>
  api.post<Assessment>(`${base}/threads/${id}/assessments`, body)

export const requestDrafts = (id: string, question?: string) =>
  api.post<{ advisorRunId: string }>(`${base}/threads/${id}/drafts/request`, { question })

export const recordDraft = (id: string, body: unknown) =>
  api.post<Draft>(`${base}/threads/${id}/drafts`, body)

export const listQueue = (p: QueueQuery = {}) =>
  api.get<PageResponse<Draft>>(`${base}/drafts${qs(p)}`)

export interface QueueQuery {
  [key: string]: string | number | boolean | undefined
  /** Matches the draft text AND its rationale — an approver remembers the argument. */
  q?: string
  /** Defaults to DRAFT. Pass APPROVED or REJECTED to read back what was decided. */
  status?: string
  tactic?: string
  page?: number
  size?: number
}

export const editDraft = (id: string, body: string) =>
  api.post<Draft>(`${base}/drafts/${id}/edit`, { body })

export const rejectDraft = (id: string, note: string) =>
  api.post<Draft>(`${base}/drafts/${id}/reject`, { note })

/**
 * The only door to a platform.
 *
 * Note what is NOT in this body: the approver. It is taken from the authenticated caller,
 * because a deployment that could name its own approver could name somebody who never saw it.
 */
export const approveDraft = (id: string, accountId: string) =>
  api.post<Deployment>(`${base}/drafts/${id}/approve`, { accountId })

/**
 * Try a failed send again.
 *
 * The dispatcher stops after a bounded number of attempts precisely so a person decides whether
 * another is sensible. This is how they say yes; the attempt count resets, because it is a new
 * decision by somebody who has seen the failure.
 */
export const resendDeployment = (id: string) =>
  api.post<Deployment>(`${base}/deployments/${id}/resend`, {})

export const cancelDeployment = (id: string) =>
  api.post<Deployment>(`${base}/deployments/${id}/cancel`)

export const listLedger = (p: LedgerQuery = {}) =>
  api.get<PageResponse<Deployment>>(`${base}/ledger${qs(p)}`)

export interface LedgerQuery {
  [key: string]: string | number | boolean | undefined
  /** Matches the posted text and the permalink. */
  q?: string
  status?: string
  accountId?: string
  page?: number
  size?: number
}

// ── configuration ───────────────────────────────────────────────────────────

export const listPersonas = () => api.get<Persona[]>(`${base}/personas`)
export const listPlatforms = () => api.get<Platform[]>(`${base}/platforms`)
export const listAccounts = () => api.get<Account[]>(`${base}/accounts`)
export const saveAccount = (body: unknown) => api.post<Account>(`${base}/accounts`, body)
export const updateAccount = (id: string, body: unknown) =>
  api.post<Account>(`${base}/accounts/${id}`, body)

export const listWatches = () => api.get<Watch[]>(`${base}/watches`)
export const saveWatch = (body: unknown) => api.post<Watch>(`${base}/watches`, body)
export const updateWatch = (id: string, body: unknown) =>
  api.post<Watch>(`${base}/watches/${id}`, body)

/**
 * Ask the council a free-form question across everything captured about one subject.
 *
 * Distinct from `requestAssessment` and `requestDrafts`, which are fixed-shape recipes about
 * ONE thread and feed a queue. This answers a question in prose and feeds a decision.
 */
export const askAboutSubject = (subjectId: string, question: string) =>
  api.post<CouncilRun>(`${base}/subjects/${subjectId}/ask`, { question })

export const listSubjectQuestions = (subjectId: string) =>
  api.get<CouncilRun[]>(`${base}/subjects/${subjectId}/questions`)

export const listCoordination = (p: CoordinationQuery = {}) =>
  api.get<PageResponse<Coordination>>(`${base}/coordination${qs(p)}`)

export interface CoordinationQuery {
  [key: string]: string | number | boolean | undefined
  /** Matches the repeated phrase. */
  q?: string
  subjectId?: string
  /** Stood-down signals are included unless this is true — the row stays with its reason. */
  liveOnly?: boolean
  page?: number
  size?: number
}
export const scanCoordination = (subjectId: string) =>
  api.post<Coordination[]>(`${base}/coordination/scan/${subjectId}`)
export const standDownSignal = (id: string, reason: string) =>
  api.post<Coordination>(`${base}/coordination/${id}/stand-down`, { reason })

// ── the narrative graph ──────────────────────────────────────────────────────

/**
 * The landing board's payload — subjects, narratives and threads as one graph.
 *
 * ## Three levels, because a flat list of thousands is not a picture of anything
 *
 * The board draws whichever level the current zoom can honestly render, the way a map draws
 * countries, then counties, then streets. Every level carries its own counts, so a cluster
 * showing three of its four hundred threads still *says* four hundred.
 *
 * ## `truncated` is not a detail
 *
 * `threads` is a ranked sample. `totals` and every `threadCount` are computed over the whole
 * window. A screen that shows a sample and labels it a population is a screen that has to be
 * distrusted — read `truncated` and say so.
 */
export interface NarrativeGraph {
  generatedAt: string
  windowHours: number
  deployThreshold: number
  /** True when `threads` is a ranked sample rather than the whole window. */
  truncated: boolean
  /** Threads in the window BEFORE the cap. */
  threadTotal: number
  totals: GraphTotals
  platforms: PlatformNode[]
  subjects: SubjectNode[]
  narratives: NarrativeNode[]
  threads: ThreadNode[]
  alerts: GraphAlert[]
}

export interface GraphTotals {
  threads: number
  narratives: number
  subjects: number
  coordinated: number
  /** Threads nothing has ever read. NOT a zero-threat count. */
  unscored: number
  aboveThreshold: number
  engagements: number
  draftsAwaiting: number
  deployedToday: number
}

export interface PlatformNode {
  code: string
  label: string
  /** False where we can watch but not reply. Rendered, because it changes the plan. */
  canPost: boolean
  threads: number
  engagements: number
  velocity: number
  /** Of windowed threads, 0..1. */
  share: number
  aboveThreshold: number
}

export interface SubjectNode {
  id: string
  name: string | null
  threads: number
  narratives: number
  threatMax: number | null
  threatMean: number | null
  velocity: number
  engagements: number
  /** Share of this subject's threads the council read as hostile or negative, 0..1. */
  hostileShare: number
  platforms: string[]
}

export interface NarrativeNode {
  id: string
  subjectId: string
  subjectName: string | null
  label: string
  /** `COORDINATED` was matched by the astroturf scan. `ORGANIC` is the residue. */
  kind: 'COORDINATED' | 'ORGANIC'
  sentiment: string
  threads: number
  threatMax: number | null
  threatMean: number | null
  velocity: number
  engagements: number
  /** 0..100 for COORDINATED, null for ORGANIC — never a zero, which would read as measured. */
  coordinationConfidence: number | null
  distinctAuthors: number | null
  firstSeenAt: string | null
  lastSeenAt: string | null
  platforms: string[]
}

export interface ThreadNode {
  id: string
  narrativeId: string
  subjectId: string
  subjectName: string | null
  platformCode: string
  title: string | null
  authorHandle: string | null
  url: string | null
  /** Null means nobody has scored it. Never coalesce to zero. */
  threat: number | null
  sentiment: string | null
  framing: string | null
  velocity: number | null
  engagements: number | null
  status: string
  firstSeenAt: string
  draftCount: number
  deployedCount: number
}

/**
 * A council-derived alert. Recomputed on every read, never stored — so one disappears the
 * moment the condition it names stops being true.
 */
export interface GraphAlert {
  id: string
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  kind: string
  title: string
  /** The council's own rationale where there is one — its words, not a summary. */
  detail: string
  source: string
  subjectId: string | null
  subjectName: string | null
  narrativeId: string | null
  threadId: string | null
  threat: number | null
  raisedAt: string
}

export interface GraphQuery {
  [key: string]: string | number | boolean | undefined
  windowHours?: number
  subjectId?: string
  limit?: number
  /**
   * A `ThreatBand.code`. Selecting a numeric band EXCLUDES unread threads — they have no score
   * and cannot satisfy a range — so `totals` stays window-wide while `threadTotal` reflects the
   * filter, and the board can say what it is hiding.
   */
  band?: string
}

export const getGraph = (p: GraphQuery = {}) =>
  api.get<NarrativeGraph>(`${base}/graph${qs(p)}`)

// ── threat bands ─────────────────────────────────────────────────────────────

/**
 * A named band on the 1–10 threat scale.
 *
 * ## Served, never hard-coded
 *
 * The boundaries are derived from the room's `deploy-threshold`, so a room that decides 5 is
 * serious gets bands that say so. A filter with the ranges compiled into the bundle would go on
 * offering the old numbers under the old names with nothing on screen admitting it — which is
 * why `display` (e.g. "High · 7–8") comes from the server rather than being assembled here.
 *
 * `min`/`max` are null for `UNREAD`, which has no score at all.
 */
export interface ThreatBand {
  code: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'UNREAD'
  label: string
  /** Label with its current range, ready to render. */
  display: string
  min: number | null
  max: number | null
  note: string
}

export interface ThreatBands {
  scaleMin: number
  scaleMax: number
  deployThreshold: number
  bands: ThreatBand[]
}

export const getThreatBands = () => api.get<ThreatBands>(`${base}/threat-bands`)
